import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  HelpCircle, 
  Book, 
  MessageCircle, 
  Video,
  FileText,
  Mail,
  Phone,
  Search,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

export function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const faqs = [
    {
      question: 'How does Infervia detect regulatory changes?',
      answer: 'Infervia monitors 6 federal sources 24/7 including CMS, HHS, and Federal Register. Our AI automatically detects new documents and changes, filtering out 90% of irrelevant noise.',
      category: 'Product'
    },
    {
      question: 'How accurate is the AI analysis?',
      answer: 'Our AI analysis has a 95% accuracy rate. Every analysis is run through a 3-stage pipeline (Clinical, Financial, Compliance) with quality control verification.',
      category: 'Product'
    },
    {
      question: 'Can I customize which alerts I receive?',
      answer: 'Yes! Go to Settings > Notification Preferences to customize alerts by department, urgency level, and delivery method (email, push, or digest).',
      category: 'Features'
    },
    {
      question: 'How do I export reports?',
      answer: 'Click the "Export Report" button in the dashboard header. You can export to PDF, Excel, or Word format with customizable sections.',
      category: 'Features'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes. Infervia is HIPAA-compliant with SOC 2 certification in progress. We use enterprise-grade encryption and never store PHI.',
      category: 'Security'
    },
    {
      question: 'How much does Infervia cost?',
      answer: 'Plans start at $500/month for small clinics up to $2,500/month for major health systems. All plans include unlimited users and a 30-day free trial.',
      category: 'Billing'
    }
  ];

  const resources = [
    {
      title: 'Getting Started Guide',
      description: 'Learn the basics of Infervia in 5 minutes',
      icon: Book,
      color: 'blue'
    },
    {
      title: 'Video Tutorials',
      description: 'Watch step-by-step video guides',
      icon: Video,
      color: 'purple'
    },
    {
      title: 'Documentation',
      description: 'Comprehensive technical documentation',
      icon: FileText,
      color: 'green'
    },
    {
      title: 'API Reference',
      description: 'Integration guides and API docs',
      icon: FileText,
      color: 'orange'
    }
  ];

  return (
    <div className="flex-1 p-6 space-y-6 overflow-auto bg-background">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Help & Support</h1>
        <p className="text-muted-foreground">
          Find answers, guides, and get in touch with our support team
        </p>
      </div>

      {/* Search Bar */}
      <Card className="p-6">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search for help articles, guides, or FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 py-6 text-lg"
          />
        </div>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {resources.map((resource, index) => (
          <Card key={index} className="p-5 hover:shadow-md transition-shadow cursor-pointer">
            <div className={`p-3 bg-${resource.color}-100 rounded-lg w-fit mb-3`}>
              <resource.icon className={`w-6 h-6 text-${resource.color}-600`} />
            </div>
            <h3 className="font-semibold mb-1">{resource.title}</h3>
            <p className="text-sm text-muted-foreground mb-3">{resource.description}</p>
            <div className="flex items-center text-sm text-primary">
              <span>Learn more</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </div>
          </Card>
        ))}
      </div>

      {/* Contact Support */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Contact Support</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 border border-border rounded-lg hover:bg-secondary transition-colors text-left">
            <Mail className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="font-semibold mb-1">Email Support</h3>
            <p className="text-sm text-muted-foreground mb-2">
              Get a response within 24 hours
            </p>
            <p className="text-sm text-primary">support@infervia.com</p>
          </button>

          <button className="p-4 border border-border rounded-lg hover:bg-secondary transition-colors text-left">
            <Phone className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="font-semibold mb-1">Phone Support</h3>
            <p className="text-sm text-muted-foreground mb-2">
              Mon-Fri, 9am-5pm EST
            </p>
            <p className="text-sm text-primary">+1 (480) 524-3866</p>
          </button>

          <button className="p-4 border border-border rounded-lg hover:bg-secondary transition-colors text-left">
            <MessageCircle className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="font-semibold mb-1">Live Chat</h3>
            <p className="text-sm text-muted-foreground mb-2">
              Chat with our team now
            </p>
            <Badge className="bg-green-100 text-green-700">Online</Badge>
          </button>
        </div>
      </Card>

      {/* FAQs */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Frequently Asked Questions</h2>
          <Button variant="outline" size="sm">
            View All FAQs
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold">{faq.question}</h3>
                <Badge variant="outline" className="ml-4">
                  {faq.category}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{faq.answer}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* System Status */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">System Status</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <div>
                <p className="font-medium">All Systems Operational</p>
                <p className="text-sm text-muted-foreground">Last checked: 2 minutes ago</p>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-700">Healthy</Badge>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">API Response Time</p>
              <p className="text-xl font-bold">234ms</p>
            </div>
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Uptime (30 days)</p>
              <p className="text-xl font-bold">99.8%</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Feedback */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Send Us Feedback</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Help us improve Infervia by sharing your thoughts and suggestions
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="feedback">Your Feedback</Label>
            <textarea
              id="feedback"
              rows={4}
              placeholder="Tell us what you think..."
              className="w-full mt-2 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <Button>
            <MessageCircle className="w-4 h-4 mr-2" />
            Submit Feedback
          </Button>
        </div>
      </Card>
    </div>
  );
}
